import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithGoogleIdToken } from './authApi';
import { loadGis, type CredentialResponse } from './gis';
import Icon from '../components/Icon';
import BrandLogo from '../components/BrandLogo';
import { PLAY_STORE_URL } from '../lib/links';

/**
 * Google Client ID for the Loaf web app. Authorized JavaScript origins
 * must include https://loaf.binodswain.dev (see design doc deploy notes).
 */
const GOOGLE_CLIENT_ID =
  '363231028605-lpq16up62i432uelhpahg90cre92ms9r.apps.googleusercontent.com';

/**
 * GoogleSignIn — renders the GIS button, exchanges the id_token for a
 * session cookie via POST /auth/web, then redirects to /app/home.
 *
 * Presentation follows the Obsidian Companion signin design: a full-screen
 * centered two-panel card. The left panel carries the Loaf brand, headline,
 * and the GIS button (rendered into a ref container). The right panel is a
 * decorative context panel shown on md+ screens.
 */
export default function GoogleSignIn() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'signing-in'>(
    'loading',
  );

  const expired = searchParams.get('expired') === '1';

  const handleCredential = useCallback(
    async (response: CredentialResponse) => {
      setStatus('signing-in');
      setError(null);
      try {
        await signInWithGoogleIdToken(response.credential);
        navigate('/app/home', { replace: true });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Sign-in failed. Please try again.';
        setError(message);
        setStatus('ready');
      }
    },
    [navigate],
  );

  useEffect(() => {
    let cancelled = false;
    loadGis()
      .then((id) => {
        if (cancelled || !buttonContainerRef.current) return;
        id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredential,
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        id.renderButton(buttonContainerRef.current, {
          theme: 'outline',
          size: 'large',
          shape: 'rectangular',
          text: 'signin_with',
          width: 280,
        });
        setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to load Google Sign-In.',
        );
        setStatus('ready');
      });
    return () => {
      cancelled = true;
    };
  }, [handleCredential]);

  return (
    <div className="flex min-h-[100dvh] w-full items-start justify-center overflow-y-auto bg-surface px-gutter py-8 text-on-surface antialiased md:py-gutter">
      <div className="flex w-full max-w-max-width-content flex-col overflow-hidden rounded-xl bg-surface-container-lowest shadow-2xl md:min-h-[819px] md:flex-row md:border border-outline-variant/30">
        {/* Primary left side — auth */}
        <div className="relative z-10 flex w-full flex-col justify-center bg-surface p-gutter md:h-full md:w-[50%] md:px-12 lg:w-[45%] lg:px-24">
          <div className="mb-gap-section flex items-center gap-3">
            <BrandLogo size={36} withText />
          </div>

          <h1 className="font-headline-lg text-headline-lg text-on-surface mb-2">
            Sign in to Loaf
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mb-gap-section">
            Review available history and manage keyboard snippets alongside Loaf voice input and AI writing.
          </p>

          {expired && (
            <div
              role="alert"
              className="mb-gap-stack rounded-lg bg-error-container px-3 py-2 font-body-sm text-body-sm text-error border border-outline-variant/30"
            >
              Your session expired, sign in again.
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="mb-gap-stack rounded-lg bg-error-container px-3 py-2 font-body-sm text-body-sm text-error border border-outline-variant/30"
            >
              {error}
            </div>
          )}

          {/* GIS button — rendered into the ref container, wrapped to fit the Obsidian panel */}
          <div className="w-full rounded-lg border border-outline-variant bg-surface-container p-4 flex items-center justify-center min-h-[64px]">
            <div ref={buttonContainerRef} className="flex justify-center" />
          </div>

          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex min-h-11 items-center justify-center gap-2 rounded-md px-3 py-2 font-label-md text-label-md text-primary transition-colors hover:underline"
          >
            <Icon name="android" className="text-[18px]" />
            <span>Get Loaf for Android</span>
            <Icon name="open_in_new" className="text-[14px]" />
          </a>

          {status === 'signing-in' && (
            <p className="mt-4 text-center font-body-sm text-body-sm text-on-surface-variant">
              Signing in…
            </p>
          )}
          {status === 'loading' && (
            <p className="mt-4 text-center font-body-sm text-body-sm text-on-surface-variant">
              Loading Google Sign-In…
            </p>
          )}
        </div>

        {/* Secondary right side — context panel */}
        <div className="hidden md:flex flex-col md:w-[50%] lg:w-[55%] bg-surface-container-low p-12 lg:p-24 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.03] pointer-events-none"
            style={{
              backgroundImage: 'radial-gradient(#a6c8ff 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          />
          <div className="flex-1 flex flex-col justify-center max-w-md mx-auto z-10">
            <h2 className="font-headline-md text-headline-md text-on-surface mb-8">
              What you can do with Loaf
            </h2>

            <div className="space-y-6">
              <div className="flex items-start gap-4 p-4 rounded-xl border border-transparent">
                <div className="p-2 rounded-lg bg-surface border border-outline-variant/50 mt-1">
                  <Icon name="graphic_eq" fill className="text-primary text-xl" />
                </div>
                <div>
                  <h3 className="font-title-md text-title-md text-on-surface mb-1">Voice input</h3>
                  <p className="font-body-sm text-body-sm text-on-surface-variant leading-relaxed">
                    Dictate text into fields from the Loaf Android keyboard.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-xl border border-transparent">
                <div className="p-2 rounded-lg bg-surface border border-outline-variant/50 mt-1">
                  <Icon name="edit_document" fill className="text-secondary text-xl" />
                </div>
                <div>
                  <h3 className="font-title-md text-title-md text-on-surface mb-1">AI writing</h3>
                  <p className="font-body-sm text-body-sm text-on-surface-variant leading-relaxed">
                    Rewrite, proofread, and compose text with AI assistance when available.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-xl border border-transparent">
                <div className="p-2 rounded-lg bg-surface border border-outline-variant/50 mt-1">
                  <Icon name="sync" fill className="text-tertiary text-xl" />
                </div>
                <div>
                  <h3 className="font-title-md text-title-md text-on-surface mb-1">
                    Synced history and snippets
                  </h3>
                  <p className="font-body-sm text-body-sm text-on-surface-variant leading-relaxed">
                    Review cloud history and manage keyboard snippets here when sync is enabled.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-auto pt-12 border-t border-outline-variant/20 flex justify-end items-center z-10 opacity-70">
            <div className="flex gap-4">
              <a
                className="font-label-sm text-label-sm text-on-surface-variant hover:text-primary transition-colors"
                href="https://loaf.binodswain.dev"
                rel="noreferrer"
                target="_blank"
              >
                Documentation
              </a>
              <Link to="/privacy" className="font-label-sm text-label-sm text-on-surface-variant hover:text-primary transition-colors">
                Privacy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
